import React, { useEffect, useRef, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'

import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { TextInput } from '@/components/forms/TextInput'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { findActiveToken, insertToken, toFieldReference } from '../helpers/fieldTokens.helpers'

const MAX_LENGTH = 5000
// Commit to Formik shortly after typing stops (and immediately on blur). Typing
// only updates local state, so keystrokes don't re-render the whole field list
// (react-beautiful-dnd rows, per-row react-selects, tooltips) each character.
const COMMIT_DEBOUNCE_MS = 250

const EMPTY_MESSAGE = 'Prompt cannot be empty'

// Keys the open suggestion list consumes in handleKeyDown, which must not also
// re-derive the active token on their keyup. trackCaret resets the highlight to
// the first entry, so an ArrowDown moved the selection and the keyup that
// followed it snapped straight back — the list looked stuck on the top value.
//
// Only the keys that leave the list OPEN belong here. Enter and Tab pick a
// suggestion and Escape dismisses it; all three close the list on keydown, and
// `dismissed` already stops their keyup from reopening it. Caret-moving keys
// (arrows left/right, Home, End) are deliberately absent: they really can move
// out of a token, so they should re-derive.
const LIST_NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp'])

// A row's free-text value. `singleLine` swaps the textarea for a short input and
// `label` heads it — for a value that's a key rather than prose (a metadata
// field's path), where a three-row textarea overstates what's being asked for.
//
// `disabled` and `readOnly` both lock the value, and differ in what the reader
// can still do with it. Chrome sends no mouse events to a disabled control, so a
// disabled textarea can't be scrolled, dragged taller, or have its text
// selected — everything past its third row is simply unreachable. `readOnly`
// locks the value while keeping all of that, so it's what locked history (an
// older version) uses; `disabled` is for a row that could still become editable
// here (an inherited or Summary field).
export const PromptDescriptionField = ({
  name,
  value,
  onCommit,
  disabled,
  readOnly = false,
  placeholder,
  testId,
  showInheritedTooltip,
  inheritedTooltip,
  singleLine = false,
  label,
  // Names the control where a visible label would be redundant — the branch
  // list already heads each row with the field's own name.
  ariaLabel,
  emptyMessage = EMPTY_MESSAGE,
  referenceError,
  tokenSuggestionsFor,
}) => {
  const [draft, setDraft] = useState(value ?? '')
  const isFocused = useRef(false)
  const debounceId = useRef(null)
  const inputRef = useRef(null)
  // The `$token` being typed, and which suggestion the keyboard is on. Held
  // together so a keystroke can't leave a stale highlight pointing past the end
  // of a freshly narrowed list.
  const [active, setActive] = useState(null)
  const [highlight, setHighlight] = useState(0)
  // Separate from the isFocused ref above, which deliberately avoids a render
  // so the value-sync effect can read focus without causing one. This drives
  // the hint, which has to re-render to appear.
  const [focused, setFocused] = useState(false)
  // Escape has to outlast the keyup that follows it: keyup re-derives the active
  // token from the caret, which would reopen the list the same keystroke closed.
  // Cleared by the next edit, so typing on brings it back.
  const dismissed = useRef(false)

  const suggestions = isNil(active) || isNil(tokenSuggestionsFor) ? [] : tokenSuggestionsFor(active.query) // prettier-ignore
  const isOpen = !isEmpty(suggestions)

  // Pull in external changes (flow load, restore/revert) unless the user is
  // actively editing, so we never clobber in-progress text. A locked field
  // can't be mid-edit (e.g. a revert re-locks the row), so always accept the
  // external value there. A read-only one counts as locked rather than as
  // editing: it holds focus while its text is scrolled, but it fires no
  // onChange, so its draft can only ever diverge from `value` because the flow
  // itself changed — exactly when it should update. Treating focus as editing
  // instead would strand stale text in a field the reader had clicked into.
  const locked = disabled || readOnly
  useEffect(() => {
    if (value !== draft && (locked || !isFocused.current)) {
      setDraft(value ?? '')
    }
  }, [value, locked])

  useEffect(() => () => clearTimeout(debounceId.current), [])

  const commit = (next) => {
    clearTimeout(debounceId.current)
    if (next !== value) {
      onCommit(next)
    }
  }

  const trackCaret = (element) => {
    if (isNil(tokenSuggestionsFor) || dismissed.current) {
      return
    }
    setActive(findActiveToken(element.value, element.selectionStart))
    setHighlight(0)
  }

  const handleChange = (event) => {
    const next = event.target.value
    setDraft(next)
    dismissed.current = false
    trackCaret(event.target)
    clearTimeout(debounceId.current)
    debounceId.current = setTimeout(() => commit(next), COMMIT_DEBOUNCE_MS)
  }

  const handleBlur = () => {
    isFocused.current = false
    setFocused(false)
    // Closed on blur rather than on click-away: a mousedown on a suggestion
    // blurs the textarea, so the list is dismissed by the pick itself.
    setActive(null)
    commit(draft)
  }

  const choose = (field) => {
    const { text, caret } = insertToken(draft, active, field.key)
    setDraft(text)
    dismissed.current = true
    setActive(null)
    commit(text)
    // Put the caret after the token rather than at the end, so typing carries
    // on where the author was.
    window.requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(caret, caret)
    })
  }

  // Typing `$` is the whole interaction, but nothing on screen says so — this
  // makes it a thing you can press rather than a keystroke you have to know.
  const insertTrigger = () => {
    const element = inputRef.current
    const caret = element?.selectionStart ?? draft.length
    const next = `${draft.slice(0, caret)}$${draft.slice(caret)}`
    setDraft(next)
    dismissed.current = false
    commit(next)
    // Opened from the text we just built rather than by re-reading the box:
    // the DOM hasn't committed the new value yet, so reading it back would
    // find the old one and leave the list shut.
    setActive(findActiveToken(next, caret + 1))
    setHighlight(0)
    window.requestAnimationFrame(() => {
      element?.focus()
      element?.setSelectionRange(caret + 1, caret + 1)
    })
  }

  const handleKeyDown = (event) => {
    if (!isOpen) {
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : suggestions.length - 1
      setHighlight((current) => (current + step) % suggestions.length)
      return
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      choose(suggestions[highlight])
      return
    }
    if (event.key === 'Escape') {
      // Only the list closes — Escape shouldn't also reach whatever modal or
      // drawer the editor happens to be inside.
      event.stopPropagation()
      dismissed.current = true
      setActive(null)
    }
  }

  // Read-only history is flagged too, even though it can't be corrected here:
  // restoring a version is gated on these same checks, so the message is what
  // names the field holding the restore back.
  const tooLong = draft.length > MAX_LENGTH
  // Whitespace-only is empty: it's no more usable as a prompt than a blank box,
  // and as a metadata path the backend rejects it outright.
  const emptyValue = isEmpty(draft.trim())
  // A `$token` naming no field is reported against the text that holds it —
  // that is the only place the author can fix it.
  const hasReferenceError = !isEmpty(referenceError)
  const invalid = Boolean(tooLong || emptyValue || hasReferenceError)

  const sharedProps = {
    ref: inputRef,
    name,
    value: draft,
    onFocus: () => {
      isFocused.current = true
      setFocused(true)
    },
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onKeyUp: (event) => {
      if (isOpen && LIST_NAVIGATION_KEYS.has(event.key)) {
        return
      }
      trackCaret(event.target)
    },
    onClick: (event) => trackCaret(event.target),
    onBlur: handleBlur,
    disabled,
    readOnly,
    placeholder,
    // Names the control for screen readers. The visible label can't do it via
    // htmlFor: both the playbook and org-defaults editors stay mounted at once
    // and render the same field names, so an id built from `name` would collide.
    ...(isEmpty(ariaLabel ?? label) ? {} : { 'aria-label': ariaLabel ?? label }),
    'data-testid': testId,
  }

  // Both variants render a sized `.field` wrapper (FormField) around a
  // full-width control so the `.rtn-pair-value` width rules land on the wrapper.
  const control = singleLine ? (
    <FormField className="rtn-pair-value rtn-pair-value--single" error={invalid}>
      <TextInput type="text" error={invalid} {...sharedProps} />
    </FormField>
  ) : (
    <FormField className="rtn-pair-value" error={invalid}>
      <TextArea rows={3} error={invalid} {...sharedProps} />
    </FormField>
  )

  // Only while writing, and never over the top of the list it exists to reach.
  const showHint = !isNil(tokenSuggestionsFor) && focused && !isOpen && !locked

  const suggestionList = isOpen ? (
    <ul
      className="rtn-token-suggestions"
      role="listbox"
      data-testid={`${testId}-token-suggestions`}
    >
      {suggestions.map((field, position) => (
        <li key={field.key}>
          <button
            type="button"
            role="option"
            aria-selected={position === highlight}
            className={classNames('rtn-token-suggestion', {
              'rtn-token-suggestion--active': position === highlight,
            })}
            // Blur would close the list before click ever fired.
            onMouseDown={(event) => {
              event.preventDefault()
              choose(field)
            }}
            onMouseEnter={() => setHighlight(position)}
          >
            <span className="rtn-token-suggestion__token">{toFieldReference(field.key)}</span>
            <span className="rtn-token-suggestion__name">{field.key}</span>
          </button>
        </li>
      ))}
    </ul>
  ) : null

  return (
    <>
      {!isEmpty(label) && <label className="rtn-pair-value-label">{label}</label>}
      {showInheritedTooltip ? (
        <InteractiveTooltip content={inheritedTooltip} placement="top" block>
          {control}
        </InteractiveTooltip>
      ) : (
        control
      )}
      {suggestionList}
      {showHint && (
        <p className="rtn-token-hint" data-testid={`${testId}-token-hint`}>
          Type{' '}
          <button
            type="button"
            className="rtn-token-hint__trigger"
            // Blur would close the box before the insert could read its caret.
            onMouseDown={(event) => {
              event.preventDefault()
              insertTrigger()
            }}
          >
            $
          </button>{' '}
          to use another field&apos;s answer
        </p>
      )}
      {tooLong && <p className="status-critical">Prompt must be less than 5000 characters</p>}
      {emptyValue && <p className="status-critical">{emptyMessage}</p>}
      {hasReferenceError && (
        <p className="status-critical" data-testid={`${testId}-reference-error`}>
          {referenceError}
        </p>
      )}
    </>
  )
}
